import slide5 from '@/assets/auth/Baustelle1.png';
import slide6 from '@/assets/auth/Drucker.jpg';
import slide7 from '@/assets/auth/Grillhütte.jpg';
import { cn } from '@iq/ui';
import { useEffect, useState } from 'react';

// Decorative imagery only: the slides carry an empty alt and the panel is
// hidden below `lg` in AuthLayout.
const SLIDES = [slide5, slide6, slide7];
const INTERVAL_MS = 5000;

export function AuthCarousel({
  slides = SLIDES,
  intervalMs = INTERVAL_MS,
}: {
  slides?: readonly string[];
  intervalMs?: number;
}) {
  const [current, setCurrent] = useState(0);
  // Read once at mount. Slides swap instantly (no crossfade), so reduced motion
  // only needs to halt the autoplay timer below.
  const [reduceMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true,
  );

  // Continuous autoplay; suppressed entirely under reduced motion or a single
  // slide. The functional updater keeps `current` out of the dependency list.
  useEffect(() => {
    if (slides.length <= 1 || reduceMotion) return;
    const id = setInterval(() => {
      setCurrent((c) => (c + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [slides.length, intervalMs, reduceMotion]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-muted">
      {slides.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          draggable={false}
          data-testid="auth-carousel-slide"
          className={cn(
            'absolute inset-0 h-full w-full select-none object-cover',
            i === current ? 'opacity-100' : 'opacity-0',
          )}
        />
      ))}

      {/* Scrim keeps the dots legible over light imagery. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/40 to-transparent" />

      {slides.length > 1 ? (
        <div className="absolute bottom-6 left-6 flex gap-2">
          {slides.map((src, i) => (
            <button
              key={src}
              type="button"
              data-testid="auth-carousel-dot"
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === current ? 'true' : undefined}
              onClick={() => setCurrent(i)}
              className={cn(
                'h-1 rounded-full transition-all duration-500',
                i === current ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/60',
              )}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
